import type { Strategies } from "@/../config/swr.config";
import { useMemo } from "react";

import useProtocolRewards, { type ResponseProtocolRewards, SupProtocolRewardSimplified } from "@/hooks/rewards/useProtocolRewards";
import useProtocolRewardsClaimable, { type ResponseProtocolRewardsClaimable } from "@/hooks/rewards/useProtocolRewardsClaimable";

export type SupComposedRewardStatus = "claimable" | "accruing";
export interface SupComposedReward extends SupProtocolRewardSimplified {
  type: SupComposedRewardStatus;
  id: string;
}

export interface useComposedRewardsReturn {
  isLoading: boolean;
  isError: boolean;
  data: SupComposedReward[];
  rewardsClaimable: ResponseProtocolRewardsClaimable | undefined;
  rewards: ResponseProtocolRewards | undefined;
}

export default function useComposedRewards(strategy: Strategies = "cached") {
  const { data: rewards, isLoading: isLoadingRewards, isError: isErrorRewards } = useProtocolRewards(strategy);
  const { data: rewardsClaimable, isLoading: isLoadingRewardsClaimable, isError: isErrorRewardsClaimable } = useProtocolRewardsClaimable(strategy);

  const composedData = useMemo<SupComposedReward[]>(() => {
    if (!rewards || !rewardsClaimable) {
      return [];
    }
    const composedResult = [] as SupComposedReward[];
    rewards.rewards?.forEach(reward => {
      composedResult.push({ id: reward.reward_id, type: "accruing", ...reward });
    });
    rewardsClaimable.rewards?.forEach(reward => {
      composedResult.push({ id: reward.reward_id, type: "claimable", ...reward });
    });
    return composedResult;
  }, [rewards, rewardsClaimable]);

  return useMemo(() => {
    return {
      isLoading: isLoadingRewardsClaimable || isLoadingRewards,
      isError: isErrorRewardsClaimable || isErrorRewards,
      data: composedData,
      rewardsClaimable,
      rewards,
    };
  }, [composedData, isErrorRewards, isErrorRewardsClaimable, isLoadingRewards, isLoadingRewardsClaimable, rewards, rewardsClaimable]);
}
